import type { AnamnesisTechnicalEventName } from '@/features/encounters/anamnesis/anamnesisTechnicalTelemetry'
import { recordAudioChunkReceived, recordAudioUploadStarted } from '@/shared/observability/streaming-trace-manager'
import {
  ANAMNESIS_SENTRY_TRANSACTIONS,
  withAnamnesisSentrySpan,
} from '@/shared/monitoring/sentry.client'
import { runSentrySafely } from '@/shared/monitoring/sentry.helpers'

const CHUNK_SENTRY_EVENTS = new Set<AnamnesisTechnicalEventName>([
  'transcription_chunk_confirmed',
  'streaming_restart',
  'streaming_max_session',
  'transcription_session_degraded',
])

export const mirrorChunkLifecycleToObservability = (options: {
  consultationId: string
  event: AnamnesisTechnicalEventName
  chunkId?: string
  sequence?: number
  durationMs?: number
  provider?: string
  engineAttempt?: number
}): void => {
  const { consultationId, event, chunkId } = options
  if (!consultationId.trim() || !chunkId?.trim()) return

  if (event === 'transcription_chunk_confirmed') {
    recordAudioChunkReceived({
      consultationId,
      chunkId,
      sequence: options.sequence ?? 0,
      durationMs: options.durationMs,
      provider: options.provider,
    })
  }

  if (!CHUNK_SENTRY_EVENTS.has(event)) return

  runSentrySafely(() => {
    withAnamnesisSentrySpan(
      ANAMNESIS_SENTRY_TRANSACTIONS.CHUNK_PROCESSING,
      {
        consultationId,
        chunkId,
        metadata: {
          event,
          ...(options.sequence != null ? { sequence: options.sequence } : {}),
          ...(options.provider ? { provider: options.provider } : {}),
          ...(options.durationMs != null ? { duration_ms: options.durationMs } : {}),
        },
      },
      () => undefined,
    )
  })
}

export const recordChunkUploadToObservability = (options: {
  consultationId: string
  chunkId: string
  engineAttempt?: number
}): void => {
  recordAudioUploadStarted(options)
  runSentrySafely(() => {
    withAnamnesisSentrySpan(
      ANAMNESIS_SENTRY_TRANSACTIONS.CHUNK_PROCESSING,
      {
        consultationId: options.consultationId,
        chunkId: options.chunkId,
        metadata: {
          phase: 'upload',
          ...(options.engineAttempt != null ? { engine_attempt: options.engineAttempt } : {}),
        },
      },
      () => undefined,
    )
  })
}
